/**
 * Processing fees, as returned by GET /api/processingFees.
 *
 * The server keeps them outside the sales ledger, one record per payment and per refund:
 * - `processingFee` is what OrderBuddy keeps of a card payment; it is negative on a refund record
 *   (commission handed back), so the sum over a transaction is the net fee.
 * - `refundFee` is what Adyen charged the merchant to refund a card payment (refund records only).
 * Terminal (POS) fees carry the POS transaction uuid as `transactionId`; online fees carry the
 * online `orderNumber`. Amounts are in dollars.
 *
 * `kind`, `refundFee`, `refundPspReference` and `refundFeeTotal` are missing on older servers.
 * Both figures are informational: they are never added into the transaction totals.
 */

import moment from 'moment';

export const FEE_KIND_PAYMENT = 'payment';
export const FEE_KIND_REFUND = 'refund';
// Refunds are recorded when they happen, which can be well after the payment.
export const REFUND_LOOKAHEAD_DAYS = 31;
const DATE_FORMAT = 'YYYY-MM-DD';

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizeFee = (fee) => ({
  ...fee,
  kind: fee.kind || FEE_KIND_PAYMENT,
  processingFee: toNumber(fee.processingFee),
  refundFee: toNumber(fee.refundFee),
  refundPspReference: fee.refundPspReference || null,
});

// Sums in cents so 1.43 + (-0.80) is 0.63, not 0.6299999999999999.
export const sumFeeField = (fees, field) =>
  Math.round(fees.reduce((sum, fee) => sum + toNumber(fee[field]) * 100, 0)) / 100;

export const normalizeFeesResponse = (data) => {
  if (!data || !Array.isArray(data.fees)) {
    return { total: 0, refundFeeTotal: 0, fees: [] };
  }
  const fees = data.fees.map(normalizeFee);
  return {
    total: sumFeeField(fees, 'processingFee'),
    refundFeeTotal: sumFeeField(fees, 'refundFee'),
    fees,
  };
};

// Net processing fee and refund cost of a set of fee records.
export const summariseFees = (fees) => ({
  processingFee: sumFeeField(fees, 'processingFee'),
  refundFee: sumFeeField(fees, 'refundFee'),
});

export const EMPTY_FEE_SUMMARY = Object.freeze({ processingFee: 0, refundFee: 0 });

// Order numbers of a POS transaction, whose online orders carry their own fees.
export const transactionOrderNumbers = (transaction) =>
  ((transaction && transaction.orders) || [])
    .map((order) => order && order.orderNumber)
    .filter((orderNumber) => orderNumber !== undefined && orderNumber !== null && orderNumber !== '')
    .map(String);

export const feeMatches = (fee, transactionId, orderNumbers = []) =>
  Boolean(
    (transactionId && fee.transactionId && fee.transactionId === transactionId) ||
      (fee.orderNumber && orderNumbers.includes(String(fee.orderNumber)))
  );

export const uniqueFees = (fees) => {
  const seen = new Set();
  return fees.filter((fee) => {
    if (!fee.id) {
      return true;
    }
    if (seen.has(fee.id)) {
      return false;
    }
    seen.add(fee.id);
    return true;
  });
};

// Fee summary per transaction uuid, matching each fee once.
export const feeSummariesByTransaction = (fees, transactions) => {
  const byTransactionId = new Map();
  const byOrderNumber = new Map();
  const add = (map, key, fee) => {
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(fee);
  };
  (fees || []).forEach((fee) => {
    if (fee.transactionId) {
      add(byTransactionId, fee.transactionId, fee);
    }
    if (fee.orderNumber) {
      add(byOrderNumber, String(fee.orderNumber), fee);
    }
  });

  const summaries = new Map();
  (transactions || []).forEach((transaction) => {
    if (!transaction || !transaction.uuid) {
      return;
    }
    const matched = uniqueFees([
      ...(byTransactionId.get(transaction.uuid) || []),
      ...transactionOrderNumbers(transaction).flatMap((orderNumber) => byOrderNumber.get(orderNumber) || []),
    ]);
    if (matched.length > 0) {
      summaries.set(transaction.uuid, summariseFees(matched));
    }
  });
  return summaries;
};

/**
 * Whole days to ask the server for, from `daysBefore` before `day` to `daysAfter` after it, never
 * past today and never before the start.
 */
export const feeDateWindow = (day, { daysBefore = 0, daysAfter = REFUND_LOOKAHEAD_DAYS, today = new Date() } = {}) => {
  const start = moment(day).startOf('day').subtract(daysBefore, 'day');
  const latest = moment(day).startOf('day').add(daysAfter, 'day');
  const todayStart = moment(today).startOf('day');
  let end = latest.isAfter(todayStart) ? todayStart : latest;
  if (end.isBefore(start)) {
    end = start.clone();
  }
  return { startDateStr: start.format(DATE_FORMAT), endDateStr: end.format(DATE_FORMAT) };
};

// "$1.23" / "-$0.80", or "-" when there is nothing to show.
export const formatFee = (amount, currencySymbol = '$') => {
  const number = toNumber(amount);
  if (!number) {
    return '-';
  }
  return `${number < 0 ? '-' : ''}${currencySymbol}${Math.abs(number).toFixed(2)}`;
};
